import { useEffect, useState, type ReactElement, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchUsers, fetchUserActivitySpark } from '../../api/admin.js';
import { useUrlParam } from '../../lib/useUrlTab.js';
import { nanoToUsdCompact, formatDateTime, shortId } from '../../lib/format.js';
import { Avatar } from '../../components/ui/Avatar.js';
import { Sparkline } from '../../components/charts/Sparkline.js';

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

const ROLE_CHIPS: Array<{ id: string; label: string }> = [
  { id: '', label: 'all' },
  { id: 'admin', label: 'admin' },
  { id: 'support', label: 'support' },
  { id: 'user', label: 'user' },
];

export function UsersListPage(): ReactElement {
  const navigate = useNavigate();
  // Click en cualquier celda → detalle. Guard: si el target está dentro de
  // un <a> nested (el email tiene su propio Link para preservar right-click
  // "open in new tab"), dejamos que el Link maneje la navegación.
  function openUser(id: string, e: MouseEvent<HTMLElement>): void {
    if ((e.target as HTMLElement).closest('a')) return;
    void navigate(`/users/${id}`);
  }

  // q se debouncea antes de tocar la URL para no spamear history con cada
  // keystroke (replace:true igualmente, pero evita work innecesario).
  const [qParam, setQParam] = useUrlParam('q');
  const [roleParam, setRoleParam] = useUrlParam('role');
  // Fase 39 — toggle para incluir service accounts del simulator. Default
  // OFF: el backend filtra is_synthetic=false sin esto.
  const [synFlag, setSynFlag] = useUrlParam('synthetic');
  const includeSynthetic = synFlag === '1';
  const [q, setQ] = useState(qParam ?? '');
  const role = roleParam ?? '';
  const debounced = useDebouncedValue(q, 250);

  // Persistir q solo cuando el debounce settle (no en cada tecla).
  useEffect(() => {
    setQParam(debounced || null);
  }, [debounced, setQParam]);

  const query = useQuery({
    queryKey: ['admin', 'users', { q: debounced, role, includeSynthetic }],
    queryFn: () => {
      const filters: Parameters<typeof fetchUsers>[0] = { limit: 50 };
      if (debounced) filters.q = debounced;
      if (role) filters.role = role;
      if (includeSynthetic) filters.includeSynthetic = true;
      return fetchUsers(filters);
    },
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-sub">Búsqueda por email + aggregates 7d.</p>
        </div>
      </div>
      <div className="filterbar">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="email…"
          style={{ minWidth: 240 }}
          data-testid="users-search"
        />
        <div className="group">
          <span className="label" style={{ margin: 0 }}>
            Role
          </span>
          <div className="chip-group">
            {ROLE_CHIPS.map((c) => (
              <button
                key={c.id || 'all'}
                type="button"
                className={`chip${role === c.id ? ' is-on' : ''}`}
                onClick={() => setRoleParam(c.id || null)}
                data-testid={`users-role-${c.id || 'all'}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <label className="group">
          <input
            type="checkbox"
            checked={includeSynthetic}
            onChange={(e) => setSynFlag(e.target.checked ? '1' : null)}
            data-testid="users-include-synthetic"
          />
          <span>incluir sintéticos</span>
        </label>
      </div>
      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {(query.error).message}</div>
      ) : (
        <table className="table" data-testid="users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Created</th>
              <th>Last session</th>
              <th>Activity 30d</th>
              <th className="r">7d spend</th>
              <th className="r">Balance</th>
              <th>ID</th>
            </tr>
          </thead>
          <tbody>
            {query.data.items.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted" style={{ textAlign: 'center' }}>
                  Sin usuarios
                </td>
              </tr>
            ) : (
              query.data.items.map((u) => (
                <tr
                  key={u.id}
                  className="row"
                  onClick={(e) => openUser(u.id, e)}
                  data-testid={`user-row-${u.id}`}
                >
                  <td>
                    <Link
                      to={`/users/${u.id}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                    >
                      <Avatar email={u.email} size="sm" />
                      <span>{u.email}</span>
                    </Link>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        u.role === 'admin'
                          ? 'b-info'
                          : u.role === 'support'
                            ? 'b-warn'
                            : 'b-neutral'
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="tt-tert mono">{formatDateTime(u.createdAt)}</td>
                  <td className="tt-tert mono">
                    {u.lastSessionAt ? formatDateTime(u.lastSessionAt) : '—'}
                  </td>
                  <td>
                    <UserActivitySpark userId={u.id} />
                  </td>
                  <td className="r mono">{nanoToUsdCompact(u.spend7dNano)}</td>
                  <td className="r mono">{nanoToUsdCompact(u.creditsBalanceNano)}</td>
                  <td className="mono tt-tert">{shortId(u.id)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

function UserActivitySpark({ userId }: { userId: string }): ReactElement {
  const q = useQuery({
    queryKey: ['admin', 'user', userId, 'activity-spark'],
    queryFn: () => fetchUserActivitySpark(userId),
    staleTime: 60_000,
  });
  if (q.isPending) return <span className="tt-tert mono">…</span>;
  if (q.isError || !q.data) return <span className="tt-tert">—</span>;
  const values = q.data.buckets.map((b) => b.sessionCount);
  return <Sparkline values={values} />;
}
